import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const SitemapPage = () => {
  // Define all site sections and their tools
  const siteStructure = [
    {
      category: "JSON Tools",
      description: "Essential tools for working with JSON data",
      icon: "📄",
      color: "#007bff",
      tools: [
        {
          path: "/json-beautifier",
          title: "JSON Beautifier",
          description: "Format and beautify JSON data for better readability"
        },
        {
          path: "/json-escape-unescape",
          title: "JSON Escape/Unescape",
          description: "Escape or unescape JSON strings and special characters"
        },
        {
          path: "/json-compare",
          title: "JSON Compare",
          description: "Compare two JSON objects and highlight differences"
        },
        {
          path: "/json-path-finder",
          title: "JSON Path Finder",
          description: "Find and extract values using JSONPath expressions"
        }
      ]
    },
    {
      category: "JSON Schema",
      description: "Tools for JSON Schema validation and generation",
      icon: "🔧",
      color: "#28a745",
      tools: [
        {
          path: "/schema-validator",
          title: "Schema Validator",
          description: "Validate JSON data against JSON Schema"
        },
        {
          path: "/json-to-schema",
          title: "JSON to Schema",
          description: "Generate JSON Schema from JSON data automatically"
        }
      ]
    },
    {
      category: "HTML Tools",
      description: "Tools for HTML formatting and preview",
      icon: "🌐",
      color: "#fd7e14",
      tools: [
        {
          path: "/html-beautifier",
          title: "HTML Beautifier",
          description: "Format and indent HTML code for better readability"
        },
        {
          path: "/html-viewer",
          title: "HTML Viewer",
          description: "Preview HTML code in real-time with live rendering"
        }
      ]
    },
    {
      category: "Basic Tools",
      description: "Utility tools for text processing and file generation",
      icon: "🛠️",
      color: "#6f42c1",
      tools: [
        {
          path: "/csharp-to-json",
          title: "C# Class to JSON",
          description: "Convert C# class definitions to JSON format"
        },
        {
          path: "/letter-count",
          title: "Letter Count",
          description: "Analyze text with detailed character and word statistics"
        },
        {
          path: "/x-counter",
          title: "X Counter",
          description: "Count occurrences of specific characters or patterns"
        },
        {
          path: "/dummy-files",
          title: "Dummy Files Generator",
          description: "Generate test files for development and testing",
          subPages: [
            { path: "/dummy-files/images", title: "Dummy Images" },
            { path: "/dummy-files/pdf", title: "Dummy PDF Files" }
          ]
        }
      ]
    },
    {
      category: "Developer Tools",
      description: "Advanced tools for developers and technical users",
      icon: "⚡",
      color: "#dc3545",
      tools: [
        {
          path: "/http-status-checker",
          title: "HTTP Status Checker",
          description: "Complete reference for HTTP status codes with search"
        },
        {
          path: "/qr-code-generator",
          title: "QR Code Generator",
          description: "Create custom QR codes with various options and formats"
        },
        {
          path: "/regex-tester",
          title: "Regex Tester",
          description: "Test and debug regular expressions with real-time matching"
        },
        {
          path: "/text-diff-checker",
          title: "Text Diff Checker",
          description: "Compare texts and highlight differences with multiple modes"
        },
        {
          path: "/text-replacer",
          title: "Text Replacer",
          description: "Find and replace text with regex support and bulk operations"
        },
        {
          path: "/javascript-compressor",
          title: "JavaScript Compressor",
          description: "Minify and compress JavaScript code to reduce file size"
        },
        {
          path: "/css-compressor",
          title: "CSS Compressor",
          description: "Minify and beautify CSS, SASS, LESS files online for free"
        },
        {
          path: "/jwt-decoder",
          title: "JWT Decoder",
          description: "Decode, verify, and generate JSON Web Tokens (JWT)"
        },
        {
          path: "/notepad",
          title: "Online Notepad",
          description: "Simple and clean online text editor with save and print"
        }
      ]
    }
  ];

  const additionalPages = [
    {
      category: "Information Pages",
      description: "Educational content about JSON and web technologies",
      icon: "📚",
      color: "#17a2b8",
      tools: [
        {
          path: "/what-is-json",
          title: "What is JSON?",
          description: "Learn about JSON format and its uses"
        },
        {
          path: "/what-is-json-escape",
          title: "What is JSON Escape?",
          description: "Understanding JSON string escaping"
        },
        {
          path: "/what-is-json-unescape",
          title: "What is JSON Unescape?",
          description: "Understanding JSON string unescaping"
        },
        {
          path: "/what-is-html-validator",
          title: "What is HTML Validator?",
          description: "Learn about HTML validation and best practices"
        }
      ]
    }
  ];

  const totalTools = siteStructure.reduce((total, section) => total + section.tools.length, 0);

  return (
    <div>
      <SEO 
        title="Sitemap - All Tools and Pages | JSON Tools" 
        description="Complete sitemap of all JSON tools, HTML utilities, developer tools, and resources available on our website. Find all tools organized by category."
        keywords="sitemap, json tools, html tools, developer tools, web utilities, online tools directory"
      />
      
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        {/* Header */}
        <div className="header-section" style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#333' }}>
            🗺️ Site Map
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#666', maxWidth: '600px', margin: '0 auto', lineHeight: '1.6' }}>
            Explore all {totalTools} tools and resources available on our website. 
            Everything is organized by category to help you find exactly what you need.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="stats-section" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px', 
          marginBottom: '40px' 
        }}>
          {siteStructure.map((section, index) => (
            <div key={index} style={{
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              textAlign: 'center',
              border: `3px solid ${section.color}`,
              transition: 'transform 0.2s ease'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '10px' }}>{section.icon}</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: section.color, marginBottom: '5px' }}>
                {section.category}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>
                {section.tools.length} Tools
              </div>
            </div>
          ))}
        </div>

        {/* Main Categories */}
        {siteStructure.map((section, sectionIndex) => (
          <div key={sectionIndex} className="category-section" style={{ marginBottom: '50px' }}>
            <div className="category-header" style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '25px',
              padding: '20px',
              backgroundColor: section.color,
              borderRadius: '12px',
              color: 'white'
            }}>
              <span style={{ fontSize: '2.5rem', marginRight: '15px' }}>{section.icon}</span>
              <div>
                <h2 style={{ fontSize: '1.8rem', margin: '0 0 5px 0' }}>{section.category}</h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: '1rem' }}>{section.description}</p>
              </div>
            </div>

            <div className="tools-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              {section.tools.map((tool, toolIndex) => (
                <div key={toolIndex} className="tool-card" style={{
                  padding: '20px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}>
                  <Link 
                    to={tool.path} 
                    style={{ 
                      textDecoration: 'none', 
                      color: 'inherit',
                      display: 'block'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.parentElement.style.transform = 'translateY(-2px)';
                      e.currentTarget.parentElement.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.parentElement.style.transform = 'translateY(0)';
                      e.currentTarget.parentElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    }}
                  >
                    <h3 style={{ 
                      fontSize: '1.3rem', 
                      marginBottom: '10px', 
                      color: section.color,
                      fontWeight: 'bold'
                    }}>
                      {tool.title}
                    </h3>
                    <p style={{ 
                      color: '#666', 
                      lineHeight: '1.5', 
                      margin: '0 0 15px 0',
                      fontSize: '0.95rem'
                    }}>
                      {tool.description}
                    </p>
                    <div style={{
                      display: 'inline-block',
                      padding: '6px 12px',
                      backgroundColor: section.color,
                      color: 'white',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}>
                      Visit Tool →
                    </div>
                  </Link>

                  {/* Sub-pages if any */}
                  {tool.subPages && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e9ecef' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '8px', color: '#666' }}>
                        Related Pages:
                      </div>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {tool.subPages.map((subPage, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subPage.path}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#f8f9fa',
                              color: section.color,
                              textDecoration: 'none',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              border: `1px solid ${section.color}`,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = section.color;
                              e.target.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#f8f9fa';
                              e.target.style.color = section.color;
                            }}
                          >
                            {subPage.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Additional Pages */}
        {additionalPages.map((section, sectionIndex) => (
          <div key={sectionIndex} className="category-section" style={{ marginBottom: '50px' }}>
            <div className="category-header" style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '25px',
              padding: '20px',
              backgroundColor: section.color,
              borderRadius: '12px',
              color: 'white'
            }}>
              <span style={{ fontSize: '2.5rem', marginRight: '15px' }}>{section.icon}</span>
              <div>
                <h2 style={{ fontSize: '1.8rem', margin: '0 0 5px 0' }}>{section.category}</h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: '1rem' }}>{section.description}</p>
              </div>
            </div>

            <div className="tools-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              {section.tools.map((tool, toolIndex) => (
                <div key={toolIndex} className="tool-card" style={{
                  padding: '20px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}>
                  <Link 
                    to={tool.path} 
                    style={{ 
                      textDecoration: 'none', 
                      color: 'inherit',
                      display: 'block'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.parentElement.style.transform = 'translateY(-2px)';
                      e.currentTarget.parentElement.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.parentElement.style.transform = 'translateY(0)';
                      e.currentTarget.parentElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    }}
                  >
                    <h3 style={{ 
                      fontSize: '1.3rem', 
                      marginBottom: '10px', 
                      color: section.color,
                      fontWeight: 'bold'
                    }}>
                      {tool.title}
                    </h3>
                    <p style={{ 
                      color: '#666', 
                      lineHeight: '1.5', 
                      margin: '0 0 15px 0',
                      fontSize: '0.95rem'
                    }}>
                      {tool.description}
                    </p>
                    <div style={{
                      display: 'inline-block',
                      padding: '6px 12px',
                      backgroundColor: section.color,
                      color: 'white',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}>
                      Read More →
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Footer Info */}
        <div className="footer-info" style={{
          textAlign: 'center',
          padding: '30px',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          marginTop: '40px'
        }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '15px', color: '#333' }}>
            🚀 All Tools Are Free & Privacy-Focused
          </h3>
          <p style={{ color: '#666', lineHeight: '1.6', maxWidth: '600px', margin: '0 auto' }}>
            All operations are performed locally in your browser. Your data never leaves your computer, 
            ensuring complete privacy and security. No registration required, no data collection.
          </p>
          <div style={{ marginTop: '20px' }}>
            <Link 
              to="/" 
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                backgroundColor: '#007bff',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .stats-section {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)) !important;
          }
          
          .tools-grid {
            grid-template-columns: 1fr !important;
          }
          
          .category-header {
            flex-direction: column !important;
            text-align: center;
          }
          
          .category-header span {
            margin-right: 0 !important;
            margin-bottom: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default SitemapPage; 